<template>
  <div>
    <!-- Filtros -->
    <div class="panel mb-4">
      <div class="flex flex-wrap gap-4">
        <div class="flex-1 min-w-[150px]">
          <label class="block text-xs text-gray-500 mb-1">ID</label>
          <b-input v-model="filters.id" :placeholder="$t('all')" size="is-small" @input="onIdInputChanged"></b-input>
        </div>
        <div class="flex-1 min-w-[150px]">
          <label class="block text-xs text-gray-500 mb-1">{{ $t('subcontractor') }}</label>
          <b-select
            v-model="filters.subcontractor"
            :placeholder="$t('all')"
            size="is-small"
            expanded
            :disabled="!canSelectAllPartners"
            @input="onSubcontractorFilterChanged"
          >
            <option :value="null">{{ $t('all') }}</option>
            <option v-for="sub in subcontractors" :key="sub.id" :value="sub.id">{{ sub.name }}</option>
          </b-select>
        </div>
        <div class="flex-1 min-w-[150px]">
          <label class="block text-xs text-gray-500 mb-1">{{ $t('work_order') }}</label>
          <b-select v-model="filters.workOrder" :placeholder="$t('all')" size="is-small" expanded @input="onFiltersChanged">
            <option :value="null">{{ $t('all') }}</option>
            <option v-for="order in workOrders" :key="order.id" :value="order.id">{{ order.code }}</option>
          </b-select>
        </div>
        <div class="flex-1 min-w-[150px]">
          <label class="block text-xs text-gray-500 mb-1">{{ $t('status') }}</label>
          <b-select v-model="filters.status" :placeholder="$t('all')" size="is-small" expanded @input="onFiltersChanged">
            <option :value="null">{{ $t('all') }}</option>
            <option
              v-for="statusOption in validationFlowStatusOptions"
              :key="'status_' + statusOption.value"
              :value="statusOption.value"
            >
              {{ $t(statusOption.label) }}
            </option>
          </b-select>
        </div>
        <div class="flex-1 min-w-[150px]">
          <label class="block text-xs text-gray-500 mb-1">{{ $t('date') }}</label>
          <b-datepicker
            v-model="filters.date"
            :placeholder="$t('all')"
            size="is-small"
            icon="calendar"
            icon-pack="fas"
            :month-names="$t('months_names')"
            :day-names="$t('days_names')"
            :first-day-of-week="parseInt($t('first_day_of_week'))"
            @input="onFiltersChanged"
          ></b-datepicker>
        </div>
      </div>
      <div class="mt-3">
        <b-button size="is-small" type="is-light" icon-left="eraser" icon-pack="fas" @click="clearFilters">
          {{ $t('clean_filters') }}
        </b-button>
      </div>
    </div>

    <!-- Encabezado con título y botones -->
    <div class="panel">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-semibold text-gray-700">{{ $t('certifications_list') }}</h2>
        <div class="flex gap-2">
          <b-button type="is-light" size="is-small" icon-left="eye" icon-pack="fas" @click="openViewCurrentCertificationModal">
            {{ $t('view_current_certification') }}
          </b-button>
          <b-button type="is-primary" size="is-small" icon-left="plus" icon-pack="fas" @click="openGenerateCertificationModal" v-if="project.can_flow_certifier">
            {{ $t('generate_certification') }}
          </b-button>
        </div>
      </div>

      <b-table
        :data="groupedCertifications"
        :loading="isLoadingGrouped"
        detailed
        detail-key="id"
        :opened-detailed="openedGroupIds"
        @update:openedDetailed="openedGroupIds = $event"
        paginated
        backend-pagination
        :total="groupedPagination.total"
        :per-page="groupedPagination.perPage"
        :current-page="groupedPagination.currentPage"
        @page-change="onGroupedPageChange"
        hoverable
        striped
        icon-pack="fas"
        class="text-sm"
      >
        <b-table-column field="id" label="ID" width="80" v-slot="props">
          <span class="text-blue font-medium">{{ props.row.id }}</span>
        </b-table-column>

        <b-table-column field="partner_name" :label="$t('subcontractor')" v-slot="props">
          {{ props.row.partner_name }}
        </b-table-column>

        <b-table-column field="work_order_code" :label="$t('order_number')" width="140" v-slot="props">
          {{ props.row.work_order_code }}
        </b-table-column>

        <b-table-column field="work_order_description" :label="$t('description')" v-slot="props">
          {{ props.row.work_order_description }}
        </b-table-column>

        <b-table-column field="order_amount" :label="$t('amount')" numeric width="120" v-slot="props">
          {{ formatCurrency(props.row.order_amount, props.row.currency) }}
        </b-table-column>

        <b-table-column field="certified_amount" :label="$t('certified')" numeric width="120" v-slot="props">
          {{ formatCurrency(props.row.certified_amount, props.row.currency) }}
        </b-table-column>

        <b-table-column field="pending_amount" :label="$t('pending')" numeric width="120" v-slot="props">
          {{ formatCurrency(props.row.pending_amount, props.row.currency) }}
        </b-table-column>

        <b-table-column field="progress_percentage" :label="$t('progress')" centered width="180" v-slot="props">
          <div class="cert-progress" :class="progressTone(props.row.progress_percentage)">
            <div
              class="cert-progress__fill"
              :style="{ width: clampPercent(props.row.progress_percentage) + '%' }"
            ></div>
            <span class="cert-progress__label">
              {{ Number(props.row.progress_percentage || 0).toFixed(2) }}%
            </span>
          </div>
        </b-table-column>

        <b-table-column field="certifications_count" :label="$t('number')" centered width="80" v-slot="props">
          {{ props.row.certifications_count }}
        </b-table-column>

        <b-table-column field="actions" label="" width="60" centered v-slot="props">
          <b-tooltip :label="$t('view_current_certification')" position="is-top" append-to-body>
            <b-button
              type="is-text"
              size="is-small"
              icon-left="eye"
              icon-pack="fas"
              class="text-gray-500 hover:text-blue"
              @click="viewCurrentForGroup(props.row)"
            ></b-button>
          </b-tooltip>
        </b-table-column>

        <template #detail="props">
          <div class="px-3 py-2 bg-gray-50">
            <table class="w-full text-xs border border-gray-200">
              <thead class="bg-gray-100">
                <tr>
                  <th class="px-2 py-1 text-left">{{ $t('certification') }}</th>
                  <th class="px-2 py-1 text-left">{{ $t('date_range') }}</th>
                  <th class="px-2 py-1 text-left">{{ $t('created_at') }}</th>
                  <th class="px-2 py-1 text-right">{{ $t('to_certify') }}</th>
                  <th class="px-2 py-1 text-right">{{ $t('previous') }}</th>
                  <th class="px-2 py-1 text-right">{{ $t('accumulated') }}</th>
                  <th class="px-2 py-1 text-right">{{ $t('pending') }}</th>
                  <th class="px-2 py-1 text-center">{{ $t('progress') }}</th>
                  <th class="px-2 py-1 text-center">{{ $t('status') }}</th>
                  <th class="px-2 py-1 text-center" style="width:110px;"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!props.row.certifications || props.row.certifications.length === 0">
                  <td colspan="10" class="px-2 py-3 text-center text-gray-500">{{ $t('no_results') }}</td>
                </tr>
                <tr
                  v-for="cert in (props.row.certifications || [])"
                  :key="`g-${props.row.id}-c-${cert.id}`"
                  class="border-t border-gray-100 cursor-pointer hover:bg-blue-50"
                  @click="onRowClicked(cert.id)"
                >
                  <td class="px-2 py-1 font-medium text-gray-900">{{ cert.certification }}</td>
                  <td class="px-2 py-1">
                    <span v-if="cert.date_from && cert.date_to">
                      {{ formatDate(cert.date_from) }} - {{ formatDate(cert.date_to) }}
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td class="px-2 py-1">{{ formatDate(cert.created_at) }}</td>
                  <td class="px-2 py-1 text-right">{{ formatCurrency(cert.to_certify, props.row.currency) }}</td>
                  <td class="px-2 py-1 text-right">{{ formatCurrency(cert.previous, props.row.currency) }}</td>
                  <td class="px-2 py-1 text-right">{{ formatCurrency(cert.accumulated, props.row.currency) }}</td>
                  <td class="px-2 py-1 text-right">{{ formatCurrency(cert.pending, props.row.currency) }}</td>
                  <td class="px-2 py-1 text-center">
                    <div class="cert-progress" :class="progressTone(cert.percent)">
                      <div
                        class="cert-progress__fill"
                        :style="{ width: clampPercent(cert.percent) + '%' }"
                      ></div>
                      <span class="cert-progress__label">
                        {{ Number(cert.percent || 0).toFixed(2) }}%
                      </span>
                    </div>
                  </td>
                  <td class="px-2 py-1 text-center">
                    <validation-status-label :status="cert.status" style="vertical-align: middle" />
                  </td>
                  <td class="px-2 py-1 text-center whitespace-nowrap" @click.stop>
                    <b-tooltip v-if="project.can_admin" :label="$t('regenerate')" position="is-top" append-to-body>
                      <b-button
                        type="is-text"
                        size="is-small"
                        icon-left="redo-alt"
                        icon-pack="fas"
                        class="text-gray-500 hover:text-blue"
                        :loading="isRegeneratingCertificationId === cert.id"
                        :disabled="isRegeneratingCertificationId === cert.id"
                        @click="confirmRegenerateCertificationDocument(cert)"
                      ></b-button>
                    </b-tooltip>
                    <b-tooltip :label="$t('view_document')" position="is-top" append-to-body>
                      <b-button
                        type="is-text"
                        size="is-small"
                        icon-left="file-alt"
                        icon-pack="fas"
                        class="text-gray-500 hover:text-blue"
                        :disabled="isRegeneratingCertificationId === cert.id"
                        @click="viewCertification(cert)"
                      ></b-button>
                    </b-tooltip>
                    <b-tooltip
                      v-if="canRecallCertification(cert)"
                      :label="$t('recall')" position="is-top" append-to-body
                    >
                      <b-button
                        type="is-text"
                        size="is-small"
                        icon-left="undo"
                        icon-pack="fas"
                        class="text-gray-500 hover:text-red-600"
                        :loading="isRecallingCertificationId === cert.id"
                        :disabled="isRecallingCertificationId === cert.id"
                        @click="confirmRecallCertification(cert)"
                      ></b-button>
                    </b-tooltip>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <template #empty>
          <div class="has-text-centered py-8">
            <p class="text-gray-500">{{ isLoadingGrouped ? $t('loading') : $t('no_results') }}</p>
          </div>
        </template>
      </b-table>
    </div>

    <!-- Los modales viven en la raiz de este componente -->
    <b-modal
      v-model="isViewCurrentModalActive"
      :can-cancel="['x', 'outside']"
      has-modal-card
      :width="900"
      custom-class="view-current-certification-modal"
      scroll="keep"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('view_current_certification') }}</p>
        </header>

        <section class="modal-card-body">
          <b-field :label="$t('subcontractor')" :message="isLoadingViewModalData ? $t('loading') : ''">
            <b-select
              v-model="viewCurrentForm.partnerId"
              expanded
              size="is-small"
              :placeholder="$t('select')"
              :loading="isLoadingViewModalData"
              :disabled="isLoadingViewModalData || !canSelectAllPartners"
              @input="onViewCurrentPartnerChanged"
            >
              <option :value="null" disabled>{{ $t('select') }}</option>
              <option v-for="partner in viewCurrentPartners" :key="'view_partner_' + partner.id" :value="partner.id">
                {{ partner.name }}
              </option>
            </b-select>
          </b-field>

          <b-field :label="$t('work_order')">
            <b-select
              v-model="viewCurrentForm.orderId"
              expanded
              size="is-small"
              :placeholder="$t('select_work_order')"
              :loading="isLoadingViewModalData"
              :disabled="isLoadingViewModalData"
            >
              <option :value="null" disabled>{{ $t('select') }}</option>
              <option v-for="order in viewCurrentOrders" :key="'view_order_' + order.id" :value="order.id">
                {{ order.code || ('#' + order.id) }}
              </option>
            </b-select>
          </b-field>

        </section>

        <footer class="modal-card-foot" style="justify-content: flex-end;">
          <b-button size="is-small" @click="closeViewCurrentCertificationModal" :disabled="isViewingCurrentCertification">
            {{ $t('cancel') }}
          </b-button>
          <b-button
            type="is-primary"
            size="is-small"
            @click="viewCurrentCertificationFromModal"
            :loading="isViewingCurrentCertification"
            :disabled="isLoadingViewModalData || isViewingCurrentCertification"
          >
            {{ $t('view_current_certification') }}
          </b-button>
        </footer>
      </div>
    </b-modal>

    <b-modal
      v-model="isGenerateModalActive"
      :can-cancel="['x', 'outside']"
      has-modal-card
      :width="900"
      custom-class="generate-certification-modal"
      scroll="keep"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('generate_certification') }}</p>
        </header>

        <section class="modal-card-body">
          <b-field :label="$t('current_partner')" :message="isLoadingModalData ? $t('loading') : ''">
            <b-input v-model="generateForm.currentPartner" size="is-small" :loading="isLoadingModalData" disabled readonly></b-input>
          </b-field>

          <b-field :label="$t('work_order')">
            <b-select
              v-model="generateForm.orderId"
              expanded
              size="is-small"
              :placeholder="$t('select_work_order')"
              :loading="isLoadingModalData || isLoadingGenerateDateConstraints"
              @input="onGenerateOrderChanged"
            >
              <option :value="null" disabled>{{ $t('select') }}</option>
              <option v-for="order in currentPartnerOrders" :key="order.id" :value="order.id">
                {{ order.code || ('#' + order.id) }}
              </option>
            </b-select>
          </b-field>

          <b-field
            :label="$t('select_date_range')"
            :message="generateDateRangeHint"
          >
            <b-datepicker
              v-model="generateForm.dateRange"
              size="is-small"
              icon="calendar"
              icon-pack="fas"
              :month-names="$t('months_names')"
              :day-names="$t('days_names')"
              :first-day-of-week="parseInt($t('first_day_of_week'))"
              :disabled="isLoadingModalData || isLoadingGenerateDateConstraints || !generateForm.orderId"
              :min-date="generateDatePickerMinDate"
              range
              expanded
            ></b-datepicker>
          </b-field>


        </section>

        <footer class="modal-card-foot" style="justify-content: flex-end;">
          <b-button size="is-small" @click="closeGenerateCertificationModal" :disabled="isGeneratingCertification">
            {{ $t('cancel') }}
          </b-button>
          <b-button
            type="is-primary"
            size="is-small"
            @click="generateCertificationFromModal"
            :loading="isGeneratingCertification"
            :disabled="isLoadingModalData || isGeneratingCertification"
          >
            {{ $t('generate_certification') }}
          </b-button>
        </footer>
      </div>
    </b-modal>

    <!-- Modal regenerar certificación -->
    <b-modal
      v-model="isRegenerateModalActive"
      :can-cancel="['x', 'outside']"
      has-modal-card
      :width="600"
      scroll="keep"
      custom-class="regenerate-certification-modal"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('regenerate_certification') }}</p>
        </header>

        <section class="modal-card-body">
          <b-field :label="$t('select_date_range')">
            <b-datepicker
              v-model="regenerateForm.dateRange"
              size="is-small"
              icon="calendar"
              icon-pack="fas"
              :month-names="$t('months_names')"
              :day-names="$t('days_names')"
              :first-day-of-week="parseInt($t('first_day_of_week'))"
              :disabled="regenerateForm.isCompleted"
              range
              expanded
            ></b-datepicker>
          </b-field>
        </section>

        <footer class="modal-card-foot" style="justify-content: flex-end;">
          <b-button size="is-small" @click="isRegenerateModalActive = false" :disabled="isRegeneratingCertificationId !== null">
            {{ $t('cancel') }}
          </b-button>
          <b-button
            type="is-warning"
            size="is-small"
            @click="regenerateCertificationDocument()"
            :loading="isRegeneratingCertificationId !== null"
            :disabled="isRegeneratingCertificationId !== null"
          >
            {{ $t('regenerate') }}
          </b-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from 'axios'
import moment from 'moment'
import EventBus from '~/plugins/bus'
import validationStatusLabel from '·/components/validations/validation-status-label'

const VALIDATION_FLOW_STATUS_OPTIONS = Object.freeze([
  { value: 0, label: 'validation_status_pending' },
  { value: 1, label: 'validation_status_pending_validation' },
  { value: 2, label: 'validation_status_completed' },
  { value: 3, label: 'validation_status_rejected' },
  { value: 4, label: 'validation_status_pending_answer' },
  { value: 5, label: 'validation_status_reopened' },
  { value: 6, label: 'validation_status_closed' },
  { value: 7, label: 'validation_status_rejected_partial' },
])

export default {
  name: 'certificacion-civil',

  components: {
    validationStatusLabel,
  },

  computed: {
    ...mapGetters({
      user: 'auth/user',
      project: 'app/project',
    }),
    canSelectAllPartners() {
      return this.project.can_admin || this.project.can_certificate
    },

    generateDatePickerMinDate() {
      if (!this.existingCertificationRanges.length) {
        return null
      }

      const maxDateToMs = this.existingCertificationRanges.reduce((max, r) => {
        const d = moment(r.date_to).valueOf()
        return d > max ? d : max
      }, 0)

      return moment(maxDateToMs).add(1, 'day').startOf('day').toDate()
    },

    generateDateRangeHint() {
      if (!this.generateForm.orderId || this.isLoadingGenerateDateConstraints) {
        return ''
      }

      if (!this.generateDatePickerMinDate) {
        return ''
      }

      return this.$t('certification_date_min_hint', {
        date: moment(this.generateDatePickerMinDate).format('DD/MM/YYYY'),
      })
    },
  },

  data: () => ({
    isLoading: false,
    filters: {
      id: null,
      subcontractor: null,
      workOrder: null,
      status: null,
      date: null,
    },
    subcontractors: [],
    workOrders: [],
    certifications: [],
    groupedCertifications: [],
    isLoadingGrouped: false,
    openedGroupIds: [],
    groupedPagination: {
      currentPage: 1,
      perPage: 25,
      total: 0,
      lastPage: 1,
    },
    isViewCurrentModalActive: false,
    isLoadingViewModalData: false,
    isViewingCurrentCertification: false,
    viewCurrentForm: {
      partnerId: null,
      orderId: null,
    },
    viewCurrentPartners: [],
    viewCurrentOrders: [],
    isGenerateModalActive: false,
    isLoadingModalData: false,
    isGeneratingCertification: false,
    isRegeneratingCertificationId: null,
    isRecallingCertificationId: null,
    isRegenerateModalActive: false,
    regenerateForm: {
      flowId: null,
      dateRange: [],
      isCompleted: false,
    },
    generateForm: {
      partnerId: null,
      currentPartner: 'Partner temporal',
      orderId: null,
      dateRange: [],
    },
    currentPartnerOrders: [],
    existingCertificationRanges: [],
    isLoadingGenerateDateConstraints: false,
    filtersDebounceTimer: null,
    pagination: {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
    },
    validationFlowStatusOptions: VALIDATION_FLOW_STATUS_OPTIONS,
  }),

  mounted() {
    this.loadFiltersData()
    this.loadCertifications()

    EventBus.$on('refreshCertifications', this.onRefreshCertifications)
  },

  beforeUnmount() {
    if (this.filtersDebounceTimer) {
      clearTimeout(this.filtersDebounceTimer)
      this.filtersDebounceTimer = null
    }

    EventBus.$off('refreshCertifications', this.onRefreshCertifications)
  },

  methods: {
    onRefreshCertifications(eventData = {}) {
      if (Number(eventData.project_id) !== Number(this.project.id)) {
        return
      }

      this.pagination.currentPage = 1
      this.loadCertifications(1)
    },

    onIdInputChanged() {
      if (this.filtersDebounceTimer) {
        clearTimeout(this.filtersDebounceTimer)
      }
      this.filtersDebounceTimer = setTimeout(() => {
        this.onFiltersChanged()
      }, 1300)
    },

    onFiltersChanged() {
      this.pagination.currentPage = 1
      this.loadCertifications(1)
    },

    onSubcontractorFilterChanged(partnerId) {
      this.updateWorkOrdersBySelectedSubcontractor(partnerId)
      this.onFiltersChanged()
    },

    clearFilters() {
      this.filters.id = null
      this.filters.subcontractor = this.canSelectAllPartners
        ? null
        : ((this.subcontractors[0] && this.subcontractors[0].id) ? this.subcontractors[0].id : null)
      this.updateWorkOrdersBySelectedSubcontractor(this.filters.subcontractor)
      this.filters.workOrder = null
      this.filters.status = null
      this.filters.date = null
      this.onFiltersChanged()
    },

    buildFiltersParams() {
      const params = {}
      const id = Number(this.filters.id)
      if (this.filters.id !== null && this.filters.id !== '' && Number.isInteger(id) && id > 0) {
        params.id = id
      }
      if (this.filters.subcontractor != null) {
        params.subcontractor = this.filters.subcontractor
      }
      if (this.filters.workOrder != null) {
        params.workOrder = this.filters.workOrder
      }
      if (this.filters.status != null) {
        params.status = this.filters.status
      }
      if (this.filters.date) {
        params.date = moment(this.filters.date).format('YYYY-MM-DD')
      }
      return params
    },

    buildUniqueWorkOrdersFromPartners(partners = []) {
      const map = new Map()
      const currentProjectId = Number(this.project.id)
      partners.forEach((partner) => {
        const orders = partner.orders || []
        orders.forEach((order) => {
          const orderProjectId = Number(order && order.project_id)
          if (order && order.id && orderProjectId === currentProjectId && !map.has(order.id)) {
            map.set(order.id, order)
          }
        })
      })
      return Array.from(map.values())
    },

    updateWorkOrdersBySelectedSubcontractor(partnerId) {
      // Para collaborator, el selector está bloqueado y siempre usa su propio partner.
      if (!this.canSelectAllPartners) {
        const currentPartner = this.subcontractors[0] || null
        this.workOrders = currentPartner
          ? (currentPartner.orders || []).filter(order => Number(order.project_id) === Number(this.project.id))
          : []
        return
      }

      // Admin/certifier: si no hay partner seleccionado, mostrar todas las ordenes del proyecto.
      if (!partnerId) {
        this.workOrders = this.buildUniqueWorkOrdersFromPartners(this.subcontractors)
      } else {
        const selectedPartner = this.subcontractors.find(partner => Number(partner.id) === Number(partnerId))
        this.workOrders = selectedPartner
          ? (selectedPartner.orders || []).filter(order => Number(order.project_id) === Number(this.project.id))
          : []
      }

      // Si la orden seleccionada no pertenece al nuevo subcontratista seleccionado, la limpiamos.
      if (this.filters.workOrder != null && !this.workOrders.some(order => Number(order.id) === Number(this.filters.workOrder))) {
        this.filters.workOrder = null
      }
    },

    async loadFiltersData() {
      if (this.canSelectAllPartners) {
        await this.loadAllProjectPartnersForFilters()
        return
      }

      await this.loadCurrentPartnerForFilters()
    },

    async loadAllProjectPartnersForFilters() {
      this.subcontractors = []
      this.workOrders = []
      this.filters.subcontractor = null

      try {
        const { data } = await axios.get(`/api/v2/project/${this.project.id}/partners`, {
          params: {
            with_orders: 1,
          },
        })

        const partners = (data && data.data) ? data.data : []
        this.subcontractors = partners
        this.updateWorkOrdersBySelectedSubcontractor(this.filters.subcontractor)
      } catch (error) {
        this.subcontractors = []
        this.workOrders = []
        this.$notify.error('error_loading_data')
      }
    },

    async loadCurrentPartnerForFilters() {
      this.subcontractors = []
      this.workOrders = []

      try {
        const { data } = await axios.get(`/api/v2/project/${this.project.id}/partners/current`)
        if (data && data.success && data.data) {
          const partner = data.data
          this.subcontractors = [partner]
          this.workOrders = (partner.orders || []).filter(order => Number(order.project_id) === Number(this.project.id))
          this.filters.subcontractor = partner.id || null
        }
      } catch (error) {
        this.subcontractors = []
        this.workOrders = []
        this.filters.subcontractor = null
        this.$notify.error('error_loading_data')
      }
    },

    async openViewCurrentCertificationModal() {
      this.isViewCurrentModalActive = true
      await this.loadViewCurrentModalData()
    },

    async viewCurrentForGroup(row) {
      if (!row || !row.partner_id || !row.work_order_id) return
      this.isViewCurrentModalActive = true
      await this.loadViewCurrentModalData()
      this.viewCurrentForm.partnerId = row.partner_id
      this.onViewCurrentPartnerChanged(row.partner_id)
      this.viewCurrentForm.orderId = row.work_order_id
    },

    closeViewCurrentCertificationModal() {
      this.isViewCurrentModalActive = false
    },

    onViewCurrentPartnerChanged(partnerId) {
      const selectedPartner = this.viewCurrentPartners.find(partner => partner.id === partnerId)
      const partnerOrders = selectedPartner ? (selectedPartner.orders || []) : []
      this.viewCurrentOrders = partnerOrders.filter(order => Number(order.project_id) === Number(this.project.id))
      this.viewCurrentForm.orderId = null
    },

    async loadViewCurrentModalData() {
      this.isLoadingViewModalData = true
      this.viewCurrentPartners = []
      this.viewCurrentOrders = []
      this.viewCurrentForm.partnerId = null
      this.viewCurrentForm.orderId = null

      try {
        if (this.canSelectAllPartners) {
          const { data } = await axios.get(`/api/v2/project/${this.project.id}/partners`, {
            params: {
              with_orders: 1,
            },
          })
          this.viewCurrentPartners = (data && data.data) ? data.data : []
        } else {
          const { data } = await axios.get(`/api/v2/project/${this.project.id}/partners/current`)
          if (data && data.success && data.data) {
            const partner = data.data
            this.viewCurrentPartners = [partner]
            this.viewCurrentForm.partnerId = partner.id || null
            this.viewCurrentOrders = (partner.orders || []).filter(order => Number(order.project_id) === Number(this.project.id))
          }
        }
      } catch (error) {
        this.viewCurrentPartners = []
        this.viewCurrentOrders = []
        this.$notify.error('error_loading_data')
      } finally {
        this.isLoadingViewModalData = false
      }
    },

    async viewCurrentCertificationFromModal() {
      if (!this.viewCurrentForm.partnerId) {
        this.$notify.warning('select_subcontractor')
        return
      }

      if (!this.viewCurrentForm.orderId) {
        this.$notify.warning('select_work_order')
        return
      }

      this.isViewingCurrentCertification = true
      try {
        const payload = {
          partner_id: this.viewCurrentForm.partnerId,
          order_id: this.viewCurrentForm.orderId,
        }
        const { data } = await axios.post(`/api/v2/project/${this.project.id}/certifications/view-current`, payload)

        if (!data.success) {
          this.$notify.error(data.error || 'error')
          return
        }

        const url = data.documentUrl || data.docUrl
        if (!url) {
          this.$notify.error('error_loading_data')
          return
        }

        window.open(url, '_blank')
        this.closeViewCurrentCertificationModal()
      } catch (error) {
        this.$notify.error('error_loading_data')
      } finally {
        this.isViewingCurrentCertification = false
      }
    },

    clampPercent(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return 0
      if (n < 0) return 0
      if (n > 100) return 100
      return n
    },
    progressTone(value) {
      const n = Number(value) || 0
      if (n >= 100) return 'cert-progress--complete'
      if (n >= 60) return 'cert-progress--high'
      if (n >= 25) return 'cert-progress--mid'
      return 'cert-progress--low'
    },

    formatDate(date) {
      return moment(date).format('DD/MM/YY')
    },

    // Símbolo de moneda por fila (currency del grupo); € por defecto. Antes estaba fijo en $.
    formatCurrency(value, currency = '€') {
      const sym = currency || '€'
      if (value == null) return '0 ' + sym
      return value.toLocaleString('es-ES', { minimumFractionDigits: 0 }) + ' ' + sym
    },

    viewCertification(row) {
      if (row && row.document_url) {
        window.open(row.document_url, '_blank')
        return
      }

      this.$notify.warning('error_loading_data')
    },

    confirmRegenerateCertificationDocument(row) {
      if (!row || !row.id || this.isRegeneratingCertificationId === row.id) {
        return
      }

      this.regenerateForm.flowId = row.id
      this.regenerateForm.isCompleted = row.status === 2
      this.regenerateForm.dateRange = (row.date_from && row.date_to)
        ? [new Date(row.date_from + 'T00:00:00'), new Date(row.date_to + 'T00:00:00')]
        : []
      this.isRegenerateModalActive = true
    },

    // Bloqueado en COMPLETED (2), REJECTED (3), REJECTED_PARTIAL (7).
    canRecallCertification(row) {
      if (!row || !row.id) return false
      const blocked = [2, 3, 7]
      return !blocked.includes(Number(row.status))
    },

    confirmRecallCertification(row) {
      if (!this.canRecallCertification(row)) return
      this.$buefy.dialog.confirm({
        title: this.$t('recall_certification'),
        message: this.$t('recall_certification_confirm'),
        confirmText: this.$t('recall'),
        cancelText: this.$t('cancel'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.recallCertification(row),
      })
    },

    async recallCertification(row) {
      if (!this.canRecallCertification(row)) return
      this.isRecallingCertificationId = row.id
      try {
        const { data } = await axios.post(
          `/api/v2/project/${this.project.id}/certifications/${row.id}/recall`
        )
        if (data && data.success) {
          this.$notify.success('certification_recalled')
          await this.loadCertifications(1)
        } else {
          this.$notify.warning((data && data.error) || 'error')
        }
      } catch (e) {
        const msg = (e && e.response && e.response.data && e.response.data.error) || 'error'
        this.$notify.warning(msg)
      } finally {
        this.isRecallingCertificationId = null
      }
    },

    async regenerateCertificationDocument() {
      const flowId = this.regenerateForm.flowId
      if (!flowId || this.isRegeneratingCertificationId === flowId) {
        return
      }

      let payload = {}
      if (Array.isArray(this.regenerateForm.dateRange) && this.regenerateForm.dateRange.length === 2) {
        payload.date_from = moment(this.regenerateForm.dateRange[0]).format('YYYY-MM-DD')
        payload.date_to = moment(this.regenerateForm.dateRange[1]).format('YYYY-MM-DD')
      }

      this.isRegeneratingCertificationId = flowId
      try {
        const { data } = await axios.post(`/api/v2/project/${this.project.id}/certifications/${flowId}/regenerate-document`, payload)

        if (!data || !data.success) {
          this.$notify.error(data.error || 'error')
          return
        }

        this.$notify.success('success')
        this.isRegenerateModalActive = false
        this.pagination.currentPage = 1
        await this.loadCertifications(1)
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.error) || 'error'
        this.$notify.error(msg)
      } finally {
        this.isRegeneratingCertificationId = null
      }
    },

    // Go to validationflow home
    onRowClicked(validationId) {
      this.$router.push({ name: 'validation.home', params: { validation_id: validationId } })
    },

    async openGenerateCertificationModal() {
      this.isGenerateModalActive = true
      await this.loadCurrentPartnerForModal()
    },

    closeGenerateCertificationModal() {
      this.isGenerateModalActive = false
    },

    async loadCurrentPartnerForModal() {
      this.isLoadingModalData = true
      this.generateForm.partnerId = null
      this.generateForm.currentPartner = ''
      this.generateForm.orderId = null
      this.generateForm.dateRange = []
      this.currentPartnerOrders = []
      this.existingCertificationRanges = []

      try {
        const { data } = await axios.get(`/api/v2/project/${this.project.id}/partners/current`)
        if (data && data.success && data.data) {
          const partner = data.data
          this.generateForm.partnerId = partner.id || null
          this.generateForm.currentPartner = partner.name || ''
          this.currentPartnerOrders = partner.orders || []
        } else {
          this.generateForm.partnerId = null
          this.generateForm.currentPartner = ''
          this.currentPartnerOrders = []
        }
      } catch (error) {
        this.generateForm.partnerId = null
        this.generateForm.currentPartner = ''
        this.currentPartnerOrders = []
        this.$notify.error('error_loading_data')
      }

      this.isLoadingModalData = false
    },

    onGenerateOrderChanged() {
      this.generateForm.dateRange = []
      this.loadExistingCertificationRangesForGenerate()
    },

    async loadExistingCertificationRangesForGenerate() {
      this.existingCertificationRanges = []

      if (!this.generateForm.partnerId || !this.generateForm.orderId) {
        return
      }

      this.isLoadingGenerateDateConstraints = true

      try {
        const { data } = await axios.get(`/api/v2/project/${this.project.id}/certifications`, {
          params: {
            subcontractor: this.generateForm.partnerId,
            workOrder: this.generateForm.orderId,
            per_page: 100,
            page: 1,
          },
        })

        if (!data || !data.success) {
          return
        }

        // Solo bloquean fechas las certs NO rechazadas (status 3 = REJECTED).
        this.existingCertificationRanges = (data.data || [])
          .filter((flow) => flow && flow.certification && Number(flow.status) !== 3)
          .map((flow) => flow.certification)
          .filter((cert) => cert && cert.date_from && cert.date_to)
          .map((cert) => ({
            date_from: cert.date_from,
            date_to: cert.date_to,
          }))
      } catch (error) {
        this.existingCertificationRanges = []
        this.$notify.error('error_loading_data')
      } finally {
        this.isLoadingGenerateDateConstraints = false
      }
    },

    isDateWithinExistingCertificationRanges(date) {
      if (!date) {
        return false
      }

      const day = moment(date).startOf('day')

      return this.existingCertificationRanges.some((range) => {
        const from = moment(range.date_from).startOf('day')
        const to = moment(range.date_to).startOf('day')
        return day.isSameOrAfter(from, 'day') && day.isSameOrBefore(to, 'day')
      })
    },

    isGenerateDateRangeOverlapping(dateFrom, dateTo) {
      if (!dateFrom || !dateTo) {
        return false
      }

      const rangeStart = moment(dateFrom).startOf('day')
      const rangeEnd = moment(dateTo).startOf('day')

      return this.existingCertificationRanges.some((existing) => {
        const existingStart = moment(existing.date_from).startOf('day')
        const existingEnd = moment(existing.date_to).startOf('day')

        return rangeStart.isSameOrBefore(existingEnd, 'day')
          && rangeEnd.isSameOrAfter(existingStart, 'day')
      })
    },

    async generateCertificationFromModal() {
      if (this.isGeneratingCertification) {
        return
      }

      if (!this.generateForm.orderId) {
        this.$notify.warning('select_work_order')
        return
      }

      if (!Array.isArray(this.generateForm.dateRange) || this.generateForm.dateRange.length !== 2) {
        this.$notify.warning('select_date_range')
        return
      }

      const [dateFrom, dateTo] = this.generateForm.dateRange
      if (!dateFrom || !dateTo || dateFrom.getTime() > dateTo.getTime()) {
        this.$notify.warning('invalid_date_range')
        return
      }

      if (this.generateDatePickerMinDate
        && moment(dateFrom).startOf('day').isBefore(moment(this.generateDatePickerMinDate).startOf('day'), 'day')) {
        this.$notify.warning('certification_date_min_hint', {
          date: moment(this.generateDatePickerMinDate).format('DD/MM/YYYY'),
        })
        return
      }

      if (this.isGenerateDateRangeOverlapping(dateFrom, dateTo)
        || this.isDateWithinExistingCertificationRanges(dateFrom)
        || this.isDateWithinExistingCertificationRanges(dateTo)) {
        this.$notify.warning('certification_date_range_overlaps')
        return
      }

      await this.generateCertification()
    },

    async generateCertification() {
      this.isGeneratingCertification = true
      try {
        const payload = {
          partner_id: this.generateForm.partnerId,
          order_id: this.generateForm.orderId,
          date_from: moment(this.generateForm.dateRange[0]).format('YYYY-MM-DD'),
          date_to: moment(this.generateForm.dateRange[1]).format('YYYY-MM-DD'),
        }
        const { data } = await axios.post(`/api/v2/project/${this.project.id}/certifications/generate`, payload)
        if (data.success) {
          this.$notify.success('success')
          this.closeGenerateCertificationModal()
          this.pagination.currentPage = 1
          this.loadCertifications(1)
        } else {
          this.$notify.error(data.error || 'error')
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || 'error'
        this.$notify.error(msg)
      } finally {
        this.isGeneratingCertification = false
      }
    },

    onPageChange(page) {
      this.pagination.currentPage = page
      this.loadCertifications(page)
    },

    async loadGroupedCertifications(page = this.groupedPagination.currentPage) {
      this.isLoadingGrouped = true
      try {
        const params = {
          page,
          per_page: this.groupedPagination.perPage,
          entity_type: 1, // PartnerProcessEntity::CivilWorks
        }
        if (this.filters.subcontractor) params.partner_id = this.filters.subcontractor
        if (this.filters.workOrder) params.work_order_id = this.filters.workOrder

        const { data } = await axios.get(
          `/api/v2/project/${this.project.id}/certifications/grouped-by-order`,
          { params }
        )
        this.groupedCertifications = (data && data.data) ? data.data : []
        const meta = (data && data.meta) || {}
        this.groupedPagination.currentPage = Number(meta.current_page || page || 1)
        this.groupedPagination.perPage = Number(meta.per_page || this.groupedPagination.perPage)
        this.groupedPagination.total = Number(meta.total || 0)
        this.groupedPagination.lastPage = Number(meta.last_page || 1)
      } catch (e) {
        this.groupedCertifications = []
        this.groupedPagination.total = 0
        this.groupedPagination.lastPage = 1
        this.$notify.error('error_loading_data')
      } finally {
        this.isLoadingGrouped = false
      }
    },

    onGroupedPageChange(page) {
      this.loadGroupedCertifications(page)
    },

    async loadCertifications(page = this.pagination.currentPage) {
      this.isLoading = true
      // En paralelo refrescamos la vista jerárquica (es la que muestra la UI ahora).
      this.loadGroupedCertifications()
      try {
        const filtersParams = this.buildFiltersParams()
        const { data } = await axios.get(`/api/v2/project/${this.project.id}/certifications`, {
          params: {
            page,
            per_page: this.pagination.perPage,
            entity_type: 1, // PartnerProcessEntity::CivilWorks
            ...filtersParams,
          },
        })
        if (data.success) {
          const meta = data.meta || {}
          this.pagination.currentPage = Number(meta.current_page || page || 1)
          this.pagination.lastPage = Number(meta.last_page || 1)
          this.pagination.perPage = Number(meta.per_page || this.pagination.perPage)
          this.pagination.total = Number(meta.total || 0)

          this.certifications = data.data.map(flow => {
            const cert = flow.certification || {}
            const partner = cert.partner || {}
            const workOrder = cert.work_order || {}

            return {
              id: flow.id,
              subcontractor: partner.name || '-',
              order_code: workOrder.code || '-',
              certification_number: cert.certification || '-',
              date_from: cert.date_from || null,
              date_to: cert.date_to || null,
              created_at: flow.created_at,
              previous_amount: Number(cert.previous || 0),
              accumulated_amount: Number(cert.accumulated || 0),
              percentage: Number(cert.percent || 0),
              to_certify_amount: Number(cert.to_certify || 0),
              status: Number(flow.status || 0),
              document_url: cert.document_url || null,
            }
          })
        } else {
          this.certifications = []
          this.pagination.total = 0
        }
      } catch (error) {
        this.certifications = []
        this.pagination.total = 0
        this.$notify.error('error_loading_data')
      }
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.panel {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  // El panel no crece más que su contenedor.
  max-width: 100%;
}

// La tabla no desborda el panel: si es más ancha, hace scroll horizontal interno.
// Solo overflow-x para no cortar la fila de detalle desplegable.
::v-deep .b-table {
  max-width: 100%;
}
::v-deep .b-table .table-wrapper {
  overflow-x: auto;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-4 {
  gap: 1rem;
}

.bg-orange-500 {
  background-color: #f97316;
}

.bg-yellow-700 {
  background-color: #a16207;
}

.bg-gray-400 {
  background-color: #9ca3af;
}

::v-deep .generate-certification-modal .modal-card {
  max-width: 900px;
  overflow: visible;
}

::v-deep .generate-certification-modal .modal-card-body {
  overflow: visible;
}

::v-deep .regenerate-certification-modal .modal-card {
  overflow: visible;
}

::v-deep .regenerate-certification-modal .modal-card-body {
  overflow: visible;
}

.cert-progress {
  position: relative;
  width: 100%;
  height: 20px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.04);
}
.cert-progress__fill {
  position: absolute;
  inset: 0 auto 0 0;
  background: #3b82f6;
  border-radius: 999px;
  transition: width 0.35s ease-out;
}
.cert-progress__label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: #1f2937;
  letter-spacing: 0.01em;
  font-variant-numeric: tabular-nums;
}
.cert-progress--low .cert-progress__fill    { background: #93c5fd; }
.cert-progress--mid .cert-progress__fill    { background: #3b82f6; }
.cert-progress--high .cert-progress__fill   { background: #2563eb; }
.cert-progress--complete .cert-progress__fill { background: #16a34a; }
/* En porcentajes altos la etiqueta cae sobre el azul/verde oscuro: mejora la legibilidad. */
.cert-progress--high .cert-progress__label,
.cert-progress--complete .cert-progress__label {
  color: #ffffff;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.25);
}

// Elimina bordes/padding que Buefy aplica por defecto en la fila de detalle
// para evitar que se vean las columnas del <b-table> padre "cortadas" arriba
// del contenido expandido.
::v-deep tr.detail > td {
  padding: 0 !important;
  border: none !important;
  background: #f9fafb;
}
::v-deep tr.detail .detail-container {
  padding: 0 !important;
}

// La última fila del tbody por defecto no muestra borde inferior, lo que hace
// que la tabla se vea "cortada". Forzamos un borde inferior visible.
::v-deep .b-table .table tbody tr:last-child > td {
  border-bottom: 1px solid #dbdbdb !important;
}

</style>
